
import apiClient from './apiClient';

import { UserDTO } from '../types/user.types';
import { encrypt } from '../utils/encryptionUtils';

// Types for Profile API
export interface UserProfile {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    role: string;
    roles?: string[];
    tenantId?: string;
    facilityId?: string;
    facilityIds?: string[];
    clientId?: string;
    createdAt?: string;
    updatedAt?: string;
    employeeId?: string;
    licenseNumber?: string;
    department?: string;
    jobTitle?: string;
    profilePictureUrl?: string;
    isActive?: boolean;
    emailVerified?: boolean;
}



export interface ChangePasswordRequest {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export interface UpdateUserProfileRequest {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    isActive?: boolean;
    emailVerified?: boolean;
    phone?: string;
    employeeId?: string;
    licenseNumber?: string;
    department?: string;
    jobTitle?: string;
    profilePictureUrl?: string;
    facilityIds?: string[];
    roles?: string[];
    password?: string;
}




export interface ApiResponse<T> {
    success: boolean;
    message: string;
    data?: T;
}

// Profile API Service
class ProfileService {
    private readonly BASE_PATH = '/profile';





    /**
     * Get user profile by ID
     */
    async getUserById(userId: string): Promise<UserDTO> {
        try {
            const response = await apiClient.get<UserDTO>(`/v1/users/keycloak/${userId}`);
            return response.data;
        } catch (error: any) {
            console.error('Get user profile error:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch user profile');
        }
    }

    /**
     * Change user password
     */
    async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
        try {
            // The user provided this specific endpoint in the cURL
            const authBaseUrl = process.env.REACT_APP_AUTH_BASE_URL || (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '/auth') : 'https://api.carex.medicforce.uk/auth');
            const url = `${authBaseUrl}/reset-own`;
            const encryptedData = {
                currentPassword: encrypt(passwordData.currentPassword),
                newPassword: encrypt(passwordData.newPassword),
                confirmPassword: encrypt(passwordData.confirmPassword)
            };
            const response = await apiClient.put(
                url,
                encryptedData
            );

        } catch (error: any) {
            console.error('Change password error:', error);
            throw new Error(error.response?.data?.message || 'Failed to change password');
        }
    }

    /**
     * Update user profile
     */
    async updateUserProfile(userId: string, data: UpdateUserProfileRequest): Promise<UserDTO> {
        try {
            const response = await apiClient.put<UserDTO>(`/v1/users/${userId}`, data);
            return response.data;
        } catch (error: any) {
            console.error('Update user profile error:', error);
            throw new Error(error.response?.data?.message || 'Failed to update user profile');
        }
    }

    /**
     * Upload profile avatar/photo using POST /v1/users/{userId}/profile-image
     */
    async uploadAvatar(userId: string, file: File): Promise<string> {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await apiClient.post<any>(
                `/v1/users/${userId}/profile-image`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            let url = '';
            if (typeof response.data === 'string') {
                url = response.data;
            } else if (response.data?.profilePictureUrl) {
                url = response.data.profilePictureUrl;
            } else if (response.data?.url) {
                url = response.data.url;
            } else if (response.data?.avatarUrl) {
                url = response.data.avatarUrl;
            } else if (response.data?.data) {
                const d = response.data.data;
                url = typeof d === 'string' ? d : d.profilePictureUrl || d.url || d.avatarUrl || '';
            }

            return url;
        } catch (error: any) {
            console.error('Upload avatar error:', error);
            const serverMsg = error.response?.data?.message || error.message || 'Failed to upload profile image';
            throw new Error(serverMsg);
        }
    }

    /**
     * Delete profile avatar (DELETE /v1/users/{userId}/profile-image/delete)
     */
    async deleteAvatar(userId: string): Promise<void> {
        try {
            await apiClient.delete(`/v1/users/${userId}/profile-image/delete`);
        } catch (error: any) {
            console.warn('Delete profile image error:', error);
            const serverMsg = error.response?.data?.message || error.message || 'Failed to delete profile image';
            throw new Error(serverMsg);
        }
    }

    /**
     * Get user profile image (GET /v1/users/{userId}/profile-image)
     */
    async getProfileImage(userId: string): Promise<string | null> {
        try {
            const response = await apiClient.get(`/v1/users/${userId}/profile-image`, {
                responseType: 'blob',
            });

            if (response.data && response.data instanceof Blob && response.data.size > 0) {
                const type = (response.data.type || response.headers['content-type'] || '').toLowerCase();
                
                // If it's JSON or text inside the blob
                if (type.includes('json') || type.includes('text')) {
                    const text = await response.data.text();
                    try {
                        const parsed = JSON.parse(text);
                        const url = parsed.profilePictureUrl || parsed.url || parsed.avatarUrl || parsed.data || (typeof parsed === 'string' ? parsed : null);
                        if (url) return url;
                    } catch {
                        if (text.startsWith('http://') || text.startsWith('https://') || text.startsWith('data:image')) {
                            return text;
                        }
                    }
                }

                // If it's a valid binary image blob, return object URL or base64 data URL
                return new Promise<string | null>((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        if (typeof reader.result === 'string' && reader.result.length > 50) {
                            resolve(reader.result);
                        } else {
                            try {
                                resolve(URL.createObjectURL(response.data));
                            } catch {
                                resolve(null);
                            }
                        }
                    };
                    reader.onerror = () => {
                        try {
                            resolve(URL.createObjectURL(response.data));
                        } catch {
                            resolve(null);
                        }
                    };
                    reader.readAsDataURL(response.data);
                });
            }
            return null;
        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            console.warn('Get profile image error:', error);
            return null;
        }
    }


}

// Export singleton instance
const profileService = new ProfileService();
export default profileService;
